import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import JsBarcode from "jsbarcode";
import { Printer, XCircle, ShieldCheck, X as XIcon, DownloadSimple, PaperPlaneTilt, Ruler, ClockClockwise, WarningOctagon } from "@phosphor-icons/react";

function Code128({ value, widthMm, heightMm }) {
    const ref = useRef();
    useEffect(() => {
        if (!ref.current || !value) return;
        try {
            JsBarcode(ref.current, value, {
                format: "CODE128",
                displayValue: true,
                fontSize: 8,
                textMargin: 0,
                margin: 0,
                height: Math.max(10, heightMm * 3.78 - 8),
                width: 1,
                background: "transparent",
            });
        } catch (_) {}
    }, [value, widthMm, heightMm]);
    return <svg ref={ref} style={{ width: `${widthMm}mm`, height: `${heightMm}mm`, display: "block" }} />;
}

// Guilloché border — two interlocking sine waves per edge.
// Banknote-style tamper-evident engraving that a standard office printer
// cannot faithfully reproduce (line spacing < 0.1 mm).
function GuillocheBorder({ widthMm, heightMm, bandMm = 1.2 }) {
    const samples = 96;
    const hCycles = Math.max(6, Math.floor(widthMm / 5));
    const vCycles = Math.max(4, Math.floor(heightMm / 5));
    const buildWave = (fn) => {
        let d = "";
        for (let i = 0; i <= samples; i++) {
            const { x, y } = fn(i / samples);
            d += (i === 0 ? "M" : "L") + x.toFixed(3) + " " + y.toFixed(3);
        }
        return d;
    };
    const amp = bandMm / 2 - 0.05;
    const paths = [];
    for (const phase of [0, Math.PI]) {
        paths.push(buildWave((t) => ({ x: t * widthMm, y: bandMm / 2 + amp * Math.sin(t * 2 * Math.PI * hCycles + phase) })));
        paths.push(buildWave((t) => ({ x: t * widthMm, y: heightMm - bandMm / 2 + amp * Math.sin(t * 2 * Math.PI * hCycles + phase) })));
        paths.push(buildWave((t) => ({ x: bandMm / 2 + amp * Math.sin(t * 2 * Math.PI * vCycles + phase), y: t * heightMm })));
        paths.push(buildWave((t) => ({ x: widthMm - bandMm / 2 + amp * Math.sin(t * 2 * Math.PI * vCycles + phase), y: t * heightMm })));
    }
    return (
        <svg
            data-testid="guilloche-border"
            viewBox={`0 0 ${widthMm} ${heightMm}`}
            preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 3 }}
        >
            {paths.map((d, i) => (
                <path key={i} d={d} stroke="#000" strokeWidth="0.08" fill="none" opacity="0.75" />
            ))}
        </svg>
    );
}

export default function Plates() {
    const { t } = useI18n();
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    const [revokeFor, setRevokeFor] = useState(null);
    const [reason, setReason] = useState("");
    const [batch, setBatch] = useState(new Set());
    const [batchPreset, setBatchPreset] = useState("eu-sticker");
    const [presets, setPresets] = useState([
        { key: "eu-sticker",  label: "EU windshield sticker (65x35)", w_mm: 65,  h_mm: 35 },
        { key: "sticker-lg",  label: "Large windshield (80x40)",      w_mm: 80,  h_mm: 40 },
        { key: "sticker-xl",  label: "XL windshield (100x60)",        w_mm: 100, h_mm: 60 },
        { key: "sticker-2xl", label: "2XL windshield (120x80)",       w_mm: 120, h_mm: 80 },
    ]);
    const canRevoke = ["super_admin", "approver"].includes(user?.role);
    const canPrint = ["printer_operator", "super_admin"].includes(user?.role);
    const [params, setParams] = useSearchParams();
    const gov = params.get("gov");

    const load = () => {
        const endpoint = gov ? `/plates?gov=${gov}` : "/plates";
        return api.get(endpoint).then((r) => setItems(r.data.plates));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [gov]);
    useEffect(() => {
        api.get("/plates/label-presets")
            .then((r) => { if (Array.isArray(r.data.presets) && r.data.presets.length) setPresets(r.data.presets); })
            .catch(() => {});
    }, []);

    const toggle = (id) => setBatch((s) => {
        const n = new Set(s);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });

    const doRevoke = async () => {
        try {
            await api.post(`/plates/${revokeFor.id}/revoke`, { reason });
            toast.success("Revoked");
            setRevokeFor(null); setReason("");
            load();
        } catch { toast.error(t("error")); }
    };

    const batchPrint = async () => {
        if (!batch.size) return;
        try {
            const { data } = await api.post("/plates/batch-zpl", {
                plate_ids: [...batch], preset: batchPreset,
            });
            const blob = new Blob([data.zpl], { type: "application/x-zpl" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `utal-batch-${data.count}.zpl`; a.click();
            URL.revokeObjectURL(url);
            toast.success(`Batch ZPL ready — ${data.count} plate(s)`);
            setBatch(new Set()); load();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t("error")); }
    };

    return (
        <div className="space-y-6" data-testid="plates-page">
            <div>
                <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· ISSUED PLATES</div>
                <h1 className="text-3xl font-bold tracking-tight mt-1">{t("plates")}</h1>
            </div>
            {gov && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30 text-primary rounded-sm text-xs uppercase tracking-widest" data-testid="plates-gov-badge">
                    Governorate: {gov}
                    <button onClick={() => setParams({})} className="ms-1" data-testid="plates-gov-clear"><XIcon size={12} weight="bold" /></button>
                </div>
            )}
            {canPrint && batch.size > 0 && (
                <Card className="p-3 rounded-sm border-primary/40 bg-primary/5 flex items-center justify-between flex-wrap gap-2" data-testid="batch-bar">
                    <div className="text-sm"><b>{batch.size}</b> plate(s) selected</div>
                    <div className="flex items-center gap-2">
                        <select value={batchPreset} onChange={(e) => setBatchPreset(e.target.value)} className="h-9 border border-input rounded-md bg-background px-2 text-xs" data-testid="batch-preset">
                            {presets.map((p) => (
                                <option key={p.key} value={p.key}>{p.label} · {p.w_mm}×{p.h_mm}mm</option>
                            ))}
                        </select>
                        <Button size="sm" variant="ghost" onClick={() => setBatch(new Set())} data-testid="batch-clear">{t("plateBatchClear")}</Button>
                        <Button size="sm" onClick={batchPrint} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="batch-print">
                            <DownloadSimple size={14} weight="bold" className="me-1" />Print {batch.size} as ZPL
                        </Button>
                    </div>
                </Card>
            )}
            <Card className="rounded-sm border-border overflow-hidden">
                <table className="w-full data-grid text-sm">
                    <thead className="bg-secondary text-xs uppercase tracking-widest">
                        <tr>
                            {canPrint && <th className="w-8"></th>}
                            <th className="text-start">{t("plateNumber")}</th>
                            <th className="text-start">{t("reference")}</th>
                            <th className="text-start">{t("ownerName")}</th>
                            <th className="text-start">KID</th>
                            <th className="text-start">{t("status")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">{t("noData")}</td></tr>}
                        {items.map((p) => (
                            <tr key={p.id} className="border-t border-border" data-testid={`plate-row-${p.id}`}>
                                {canPrint && (
                                    <td>
                                        {p.status === "active" && (
                                            <input type="checkbox" checked={batch.has(p.id)} onChange={() => toggle(p.id)} data-testid={`batch-check-${p.id}`} />
                                        )}
                                    </td>
                                )}
                                <td className="mono font-bold">{p.plate_number}</td>
                                <td className="mono">{p.reference}</td>
                                <td>{p.owner_name}</td>
                                <td className="mono text-xs text-muted-foreground">{p.kid}</td>
                                <td>
                                    {p.status === "active" ? (
                                        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-green-700"><ShieldCheck size={14} weight="fill" />{t("active")}</span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-primary"><XCircle size={14} weight="fill" />{t("revoked")}</span>
                                    )}
                                </td>
                                <td className="text-end space-x-2">
                                    <Button size="sm" variant="secondary" onClick={() => setSelected(p)} data-testid={`plate-view-${p.id}`}>{t("view")}</Button>
                                    {canRevoke && p.status === "active" && (
                                        <Button size="sm" variant="ghost" onClick={() => setRevokeFor(p)} data-testid={`plate-revoke-${p.id}`}>{t("revoke")}</Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="tracking-widest uppercase text-xs">{t("platePreviewTitle")}</DialogTitle>
                    </DialogHeader>
                    {selected && <PlatePreview plate={selected} canPrint={canPrint} onPrinted={load} />}
                </DialogContent>
            </Dialog>

            <Dialog open={!!revokeFor} onOpenChange={(o) => !o && setRevokeFor(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{t("revoke")} — {revokeFor?.plate_number}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Input placeholder={t("plateReason")} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="revoke-reason" />
                        <Button onClick={doRevoke} disabled={!reason} className="bg-primary hover:bg-primary/90 rounded-sm w-full" data-testid="revoke-confirm">{t("confirm")}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Ruler_Overlay({ widthMm, heightMm }) {
    const ticks = (n) => Array.from({ length: n + 1 }, (_, i) => i);
    return (
        <>
            {/* Top ruler */}
            <div className="absolute left-0 -top-4 h-3 flex" style={{ width: `${widthMm}mm` }} aria-hidden>
                {ticks(widthMm).map((i) => (
                    <div key={i} className="relative" style={{ width: "1mm" }}>
                        <div className={`absolute top-0 ${i % 10 === 0 ? "h-3 bg-black" : i % 5 === 0 ? "h-2 bg-black/70" : "h-1 bg-black/40"}`} style={{ width: "1px" }} />
                        {i % 10 === 0 && <div className="absolute -top-3 text-[6pt] font-mono" style={{ left: "-4px" }}>{i}</div>}
                    </div>
                ))}
            </div>
            {/* Left ruler */}
            <div className="absolute -left-4 top-0 w-3 flex flex-col" style={{ height: `${heightMm}mm` }} aria-hidden>
                {ticks(heightMm).map((i) => (
                    <div key={i} className="relative" style={{ height: "1mm" }}>
                        <div className={`absolute left-0 ${i % 10 === 0 ? "w-3 bg-black" : i % 5 === 0 ? "w-2 bg-black/70" : "w-1 bg-black/40"}`} style={{ height: "1px" }} />
                        {i % 10 === 0 && <div className="absolute -left-4 text-[6pt] font-mono" style={{ top: "-4px" }}>{i}</div>}
                    </div>
                ))}
            </div>
        </>
    );
}

function PlatePreview({ plate, canPrint, onPrinted }) {
    const { t, lang } = useI18n();
    const printRef = useRef();
    const [preset, setPreset] = useState("eu-sticker");
    const [showRuler, setShowRuler] = useState(true);
    const [busy, setBusy] = useState(false);
    const [showLog, setShowLog] = useState(false);
    const [logJobs, setLogJobs] = useState([]);
    const [presets, setPresets] = useState([
        { key: "eu-sticker",  label: "EU windshield sticker (65x35)", w_mm: 65,  h_mm: 35 },
        { key: "sticker-lg",  label: "Large windshield (80x40)",      w_mm: 80,  h_mm: 40 },
        { key: "sticker-xl",  label: "XL windshield (100x60)",        w_mm: 100, h_mm: 60 },
        { key: "sticker-2xl", label: "2XL windshield (120x80)",       w_mm: 120, h_mm: 80 },
    ]);
    useEffect(() => {
        api.get("/plates/label-presets").then((r) => setPresets(r.data.presets || [])).catch(() => {});
    }, []);
    const size = presets.find((p) => p.key === preset) || presets[0] || { w_mm: 100, h_mm: 50, label: "" };
    const isRevoked = plate.status === "revoked";

    const openLog = async () => {
        try {
            const { data } = await api.get(`/plates/${plate.id}/print-log`);
            setLogJobs(data.jobs || []); setShowLog(true);
        } catch { toast.error("Failed to load print log"); }
    };

    const doPrint = async () => {
        try { await api.post(`/plates/${plate.id}/print`); onPrinted?.(); } catch {}
        window.print();
    };
    const downloadZpl = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/plates/${plate.id}/zpl`, { preset, lang });
            const blob = new Blob([data.zpl], { type: "application/x-zpl" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `${plate.plate_number}.zpl`; a.click();
            URL.revokeObjectURL(url);
            toast.success("ZPL downloaded");
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed"); }
        setBusy(false);
    };
    const sendLan = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/plates/${plate.id}/zpl-send`, { preset, lang });
            toast.success(`Sent ${data.bytes} bytes to Zebra`);
            onPrinted?.();
        } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Printer error"); }
        setBusy(false);
    };
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <select value={preset} onChange={(e) => setPreset(e.target.value)} className="h-9 border border-input rounded-md bg-background px-2 text-xs" data-testid="preset-select">
                    {presets.map((p) => <option key={p.key} value={p.key}>{`${p.label} · ${p.w_mm}×${p.h_mm}mm`}</option>)}
                </select>
                <div className="flex items-center gap-3">
                    <label className="text-xs inline-flex items-center gap-1 cursor-pointer" data-testid="ruler-toggle">
                        <input type="checkbox" checked={showRuler} onChange={(e) => setShowRuler(e.target.checked)} />
                        <Ruler size={14} className="me-1" />Ruler
                    </label>
                    <Button size="sm" variant="ghost" onClick={openLog} data-testid="open-print-log">
                        <ClockClockwise size={14} className="me-1" />Print log
                    </Button>
                </div>
            </div>
            <div className="flex justify-center overflow-auto py-8">
                <div className="relative" style={{ width: `${size.w_mm}mm`, height: `${size.h_mm}mm` }}>
                    {showRuler && <Ruler_Overlay widthMm={size.w_mm} heightMm={size.h_mm} />}
                    <div ref={printRef} className="print-area">
                        <div className="border border-black bg-white text-black relative overflow-hidden shadow-md" style={{ width: `${size.w_mm}mm`, height: `${size.h_mm}mm`, boxSizing: "border-box", display: "flex", padding: "1.2mm" }}>
                            <GuillocheBorder widthMm={size.w_mm} heightMm={size.h_mm} bandMm={1.2} />
                            {/* Green expiry tab — spans full height */}
                            <div style={{ width: "22%", background: "#0f8a3a", color: "white", display: "flex", alignItems: "center", justifyContent: "center", padding: "1mm" }}>
                                <div className="mono font-black leading-none text-center" style={{ fontSize: size.h_mm < 40 ? "16pt" : "22pt", letterSpacing: "0.02em" }}>
                                    {(() => { const e = (plate.expires_at || "").slice(0, 10); return e ? `${e.slice(5, 7)}/${e.slice(2, 4)}` : "--/--"; })()}
                                </div>
                            </div>
                            {/* Right side — split vertically into main (82%) + Code128 fallback strip (18%) */}
                            <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <div style={{ flex: "0 0 82%", display: "flex", minHeight: 0 }}>
                                    {/* Microtext ribbon at the very top (absolute) */}
                                    <div data-testid="microtext-top" aria-hidden style={{ position: "absolute", top: "0.2mm", left: `calc(22% + 1mm)`, right: "1mm", fontSize: "2.4pt", letterSpacing: "0.05em", whiteSpace: "nowrap", overflow: "hidden", lineHeight: 1, opacity: 0.85, pointerEvents: "none" }} dir={lang === "ar" ? "rtl" : "ltr"}>
                                        {t("stickerMicroPhrase").repeat(40)}
                                    </div>
                                    {/* Middle text pane */}
                                    <div className="relative" style={{ flex: 1, padding: "2mm 1.5mm 1mm 1.5mm", minWidth: 0 }} dir={lang === "ar" ? "rtl" : "ltr"}>
                                        <div style={{ fontSize: "5pt", letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 700 }}>UTAL MEA</div>
                                        <div className="mono font-black" style={{ fontSize: size.h_mm < 40 ? "10pt" : "13pt", lineHeight: 1, marginTop: "0.5mm" }} dir="ltr">{plate.plate_number}</div>
                                        <div style={{ fontSize: "5pt", marginTop: "0.5mm", lineHeight: 1.2 }}>
                                            {t("stickerValidUntil")} <span className="mono" dir="ltr">{(plate.expires_at || "").slice(0, 10) || "-"}</span>
                                        </div>
                                        <div style={{ fontSize: "5pt", lineHeight: 1.2 }}>{t("stickerNo")} <span className="mono" dir="ltr">{(plate.kid || "").slice(-8)}</span></div>
                                    </div>
                                    {/* Dedicated QR column — square, matches main-pane height */}
                                    <div style={{ width: `${size.h_mm * 0.82}mm`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "1mm", gap: "0.5mm" }}>
                                        <QRCodeCanvas value={plate.signed_payload} size={size.h_mm * 0.82 * 3.78 * 0.75} level="M" includeMargin={false} />
                                        {/* Faint "COPY VOID" pantograph — visible on photocopies */}
                                        <div data-testid="copy-void-pantograph" style={{
                                            width: `${size.h_mm * 0.82 * 0.75}mm`,
                                            height: `${size.h_mm * 0.82 * 0.14}mm`,
                                            border: "0.15mm solid #000",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            backgroundImage: "repeating-linear-gradient(45deg, rgba(0,0,0,0.28) 0 0.15mm, transparent 0.15mm 0.5mm)",
                                            color: "#000",
                                            fontFamily: "monospace",
                                            fontWeight: 900,
                                            fontSize: `${size.h_mm * 0.06}pt`,
                                            letterSpacing: "0.15em",
                                            mixBlendMode: "difference",
                                        }}>
                                            <span style={{ background: "#fff", padding: "0 0.3mm" }}>{t("stickerCopyVoid")}</span>
                                        </div>
                                    </div>
                                </div>
                                {/* Microtext ribbon — thermal printers render crisply, copiers blur it */}
                                <div data-testid="microtext-ribbon" style={{ fontSize: "2.4pt", letterSpacing: "0.05em", color: "#000", padding: "0 1mm", whiteSpace: "nowrap", overflow: "hidden", lineHeight: 1, opacity: 0.85 }} dir={lang === "ar" ? "rtl" : "ltr"}>
                                    {t("stickerMicroPhrase").repeat(40)}
                                </div>
                                {/* Code128 fallback strip */}
                                <div style={{ flex: "0 0 15%", padding: "0 1.5mm 0.5mm 1.5mm", display: "flex", alignItems: "center", minHeight: 0 }} data-testid="code128-fallback">
                                    <Code128 value={plate.plate_number} widthMm={size.w_mm * 0.75} heightMm={size.h_mm * 0.13} />
                                </div>
                            </div>
                            {isRevoked && (
                                <>
                                    <div className="absolute inset-0 pointer-events-none" style={{ background: "repeating-linear-gradient(45deg, rgba(200,16,46,0.10) 0 8px, transparent 8px 16px)" }} data-testid="revoked-hatch" />
                                    <div className="absolute end-1 top-1 pointer-events-none">
                                        <span className="inline-block bg-primary text-primary-foreground font-black tracking-[0.2em] px-1.5" style={{ fontSize: "7pt" }} data-testid="revoked-banner">{t("stickerRevoked")}</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="text-[10px] mono text-muted-foreground text-center no-print">
                {size.label} — bubble ruler shows mm scale at 1:1
                {isRevoked && <div className="text-primary mt-1"><WarningOctagon size={12} weight="fill" className="inline me-1" />Plate revoked — ZPL output includes cross-hatch + REVOKED banner</div>}
            </div>
            {canPrint && (
                <div className="flex gap-2 no-print">
                    <Button onClick={doPrint} variant="secondary" className="rounded-sm flex-1" data-testid="print-plate-btn">
                        <Printer size={16} className="me-1" />Browser Print
                    </Button>
                    <Button onClick={downloadZpl} disabled={busy} variant="outline" className="rounded-sm flex-1" data-testid="download-zpl-btn">
                        <DownloadSimple size={16} className="me-1" />Download ZPL
                    </Button>
                    <Button onClick={sendLan} disabled={busy} className="rounded-sm flex-1 bg-primary hover:bg-primary/90" data-testid="send-zpl-lan-btn">
                        <PaperPlaneTilt size={16} className="me-1" />Send sticker to Zebra (LAN)
                    </Button>
                </div>
            )}
            <Dialog open={showLog} onOpenChange={(o) => !o && setShowLog(false)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle className="uppercase tracking-widest text-xs">Print log — {plate.plate_number}</DialogTitle></DialogHeader>
                    <div className="max-h-96 overflow-auto" data-testid="print-log-body">
                        {logJobs.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">{t("plateNoPrintJobs")}</div>}
                        <table className="w-full text-xs data-grid">
                            <thead className="bg-secondary text-[10px] uppercase tracking-widest">
                                <tr><th className="text-start">When</th><th className="text-start">Kind</th><th className="text-start">Preset</th><th className="text-start">By</th><th className="text-start">Status</th><th className="text-end">Bytes</th></tr>
                            </thead>
                            <tbody>
                                {logJobs.map((j, i) => (
                                    <tr key={i} className="border-t border-border" data-testid={`print-log-row-${i}`}>
                                        <td className="mono">{new Date(j.ts).toLocaleString()}</td>
                                        <td className="uppercase text-[10px] tracking-widest">{j.kind}</td>
                                        <td className="mono">{j.preset}</td>
                                        <td className="mono truncate max-w-[10rem]">{j.actor_email}</td>
                                        <td className={j.status === "ok" ? "text-green-700" : "text-primary"}>{j.status}{j.error ? ` · ${j.error.slice(0, 40)}` : ""}</td>
                                        <td className="text-end mono">{j.bytes ?? "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
